import type { Grant, PermissionDefinition } from '../api/business/types';

/**
 * Grant sets as the permission matrix edits them. The API normalises and re-checks everything (BR-10,
 * BR-11); these only keep what is ticked on screen consistent with what will be saved.
 */

export const hasGrant = (
  grants: ReadonlyArray<Grant>,
  permission: string,
  companyId: string | null,
): boolean => grants.some((grant) => grant.permission === permission && grant.companyId === companyId);

/** Ticking a permission also ticks what it implies, on the same company (or tenant-wide). */
export const withGrant = (
  grants: ReadonlyArray<Grant>,
  definitions: ReadonlyArray<PermissionDefinition>,
  permission: string,
  companyId: string | null,
): Array<Grant> => {
  const next: Array<Grant> = [...grants];
  const pending: Array<string> = [permission];

  while (pending.length > 0) {
    const code = pending.pop() as string;

    if (hasGrant(next, code, companyId)) {
      continue;
    }

    next.push({ permission: code, companyId });
    pending.push(...(definitions.find((definition) => definition.code === code)?.implies ?? []));
  }

  return next;
};

/** Unticking a permission also unticks everything that implies it — a manage without its read is not a state. */
export const withoutGrant = (
  grants: ReadonlyArray<Grant>,
  definitions: ReadonlyArray<PermissionDefinition>,
  permission: string,
  companyId: string | null,
): Array<Grant> => {
  const removed = new Set<string>([permission]);
  let grew = true;

  while (grew) {
    grew = false;

    for (const definition of definitions) {
      if (!removed.has(definition.code) && definition.implies.some((code) => removed.has(code))) {
        removed.add(definition.code);
        grew = true;
      }
    }
  }

  return grants.filter((grant) => !(grant.companyId === companyId && removed.has(grant.permission)));
};

/** Whether every grant in `grants` is also held in `holder` — the delegation rule, BR-11. */
export const isSubsetOf = (grants: ReadonlyArray<Grant>, holder: ReadonlyArray<Grant>): boolean =>
  grants.every((grant) => hasGrant(holder, grant.permission, grant.companyId));
